import React, { useState, useRef } from 'react';
import { Music, Plus, X, Loader2, Upload, Pencil } from 'lucide-react';
import SongSearchModal from './SongSearchModal';
import InfoTooltip from '../InfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const DEFAULT_CAPS = { must: 25, dont: 10, maybe: 25 };

// The three sections, in display order. `icon`/`iconColor` drive the
// icon-only buttons, headers and legend shared across the client + admin views.
export const SECTIONS = [
  { key: 'must', label: 'Must Play', short: 'Must', icon: '✓', iconColor: '#34D399', accent: '#E6C86A', hint: 'Please play these' },
  { key: 'dont', label: 'Do Not Play', short: "Don't", icon: '⦸', iconColor: '#F87171', accent: '#F19B9B', hint: 'Never play these' },
  { key: 'maybe', label: 'Play If Possible', short: 'Maybe', icon: '~', iconColor: '#60A5FA', accent: '#8FB8F2', hint: 'Nice-to-haves if there’s time' },
];

// Shared glyph renderer so ✓ ~ ⦸ look identical everywhere they appear.
export const SectionGlyph = ({ sec, className = '', color }) => (
  <span aria-hidden="true" className={`font-bold leading-none ${className}`} style={{ color: color || sec.iconColor }}>{sec.icon}</span>
);

export const songKey = (s) => `${(s.title || '').trim()}|${(s.artist || '').trim()}`.toLowerCase();

/**
 * SongSectionsPicker — the shared three-section song picker used on BOTH the
 * public client song page and the admin Planning tab. Controlled: owns no
 * persistence, just calls onChange(nextSongs). Reuses SongSearchModal (Apple/
 * iTunes search + 30s preview + one-tap add) and the photo/PDF upload-convert.
 */
export default function SongSectionsPicker({ songs = [], onChange, caps = DEFAULT_CAPS, token = '', eventTitle = '' }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [uploadNote, setUploadNote] = useState('');
  const fileRef = useRef(null);

  const capOf = (key) => caps[key] || DEFAULT_CAPS[key];
  const countOf = (key) => songs.filter((s) => s.section === key).length;
  const isFull = (key) => countOf(key) >= capOf(key);
  const sectionFullMap = { must: isFull('must'), dont: isFull('dont'), maybe: isFull('maybe') };

  const sectionOf = (song) => {
    const k = songKey(song);
    const hit = songs.find((s) => songKey(s) === k);
    return hit ? hit.section : null;
  };

  // Add a brand-new song to a section, OR move an already-added song there.
  const addToSection = (song, key) => {
    const k = songKey(song);
    const existingIdx = songs.findIndex((s) => songKey(s) === k);
    if (existingIdx !== -1) {
      if (songs[existingIdx].section === key) return;
      if (songs.filter((s) => s.section === key).length >= capOf(key)) return;
      onChange(songs.map((s, i) => (i === existingIdx ? { ...s, section: key } : s)));
      return;
    }
    if (songs.filter((s) => s.section === key).length >= capOf(key)) return;
    onChange([...songs, { title: song.title, artist: song.artist || '', artwork: song.artwork, previewUrl: song.previewUrl, section: key, source: song.source || undefined }]);
  };

  const moveSong = (idx, key) => {
    if (songs[idx].section === key) return;
    if (songs.filter((s) => s.section === key).length >= capOf(key)) return;
    onChange(songs.map((s, i) => (i === idx ? { ...s, section: key } : s)));
  };

  const removeSong = (idx) => onChange(songs.filter((_, i) => i !== idx));

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !token) return;
    setUploadNote('');
    setConverting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API}/api/song-requests/convert/${token}`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (r.ok && Array.isArray(d.songs) && d.songs.length) {
        let added = 0;
        let skipped = 0;
        const seen = new Set(songs.map((s) => songKey(s)));
        let mustLeft = capOf('must') - songs.filter((s) => s.section === 'must').length;
        let maybeLeft = capOf('maybe') - songs.filter((s) => s.section === 'maybe').length;
        const add = [];
        d.songs.forEach((s) => {
          if (!s.title) return;
          const k = songKey(s);
          if (seen.has(k)) return;
          seen.add(k);
          // Must Play first, then spill into Play If Possible, then skip.
          let section = null;
          if (mustLeft > 0) { section = 'must'; mustLeft -= 1; }
          else if (maybeLeft > 0) { section = 'maybe'; maybeLeft -= 1; }
          if (!section) { skipped += 1; return; }
          add.push({ title: s.title, artist: s.artist || '', artwork: s.artwork, previewUrl: s.previewUrl, section });
          added += 1;
        });
        if (add.length) onChange([...songs, ...add]);
        let note = `Added ${added} song${added === 1 ? '' : 's'} from your upload — review below.`;
        if (skipped > 0) note += ` Couldn't add ${skipped} — lists full.`;
        setUploadNote(note);
      } else {
        setUploadNote(d.detail || "Couldn't read that file. Try a clearer photo, or add songs by search.");
      }
    } catch (err) {
      setUploadNote('Upload failed. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  const totalSongs = songs.length;

  return (
    <div data-testid="song-sections-picker">
      {/* Shared actions — search + upload */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-[13px] font-semibold shrink-0 flex items-center gap-1.5">
          Your lists <span className="text-white/35 font-normal">({totalSongs})</span>
          <InfoTooltip side="bottom" title="Song previews" points={["30-second clips (Apple/iTunes) to check the right song", "The DJ plays the full version at the event"]} />
        </p>
        <div className="flex items-center gap-1.5">
          {token && (
            <button onClick={() => fileRef.current?.click()} disabled={converting} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/75 text-[12px] font-medium active:scale-95 transition disabled:opacity-50" title="Upload a photo or PDF of a list" data-testid="song-upload-btn">
              {converting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}Upload
            </button>
          )}
          <button onClick={() => setSearchOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-br from-[#E6C86A] to-[#C5A028] text-[#0B0F1A] text-[12px] font-semibold active:scale-95 transition" data-testid="song-search-btn"><Plus className="w-3.5 h-3.5" />Search &amp; add a song</button>
        </div>
        {token && <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload} />}
      </div>
      {(converting || uploadNote) && (
        <p className="text-[11px] mb-3 flex items-center gap-1.5 text-white/55" data-testid="song-upload-note">
          {converting ? (<><Loader2 className="w-3 h-3 animate-spin text-[#D4AF37]" />Reading the list…</>) : uploadNote}
        </p>
      )}

      {/* Legend — what the three icons mean */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[11.5px] text-white/60" data-testid="song-legend">
        {SECTIONS.map((sec, idx) => (
          <span key={sec.key} className="inline-flex items-center gap-1.5">
            {idx > 0 && <span className="text-white/20 mr-1.5">·</span>}
            <span
              className="inline-flex items-center gap-1.5"
              title={sec.label}
              aria-label={sec.label}
              data-testid={`song-legend-${sec.key}`}
            >
              <SectionGlyph sec={sec} className="text-[13px]" />
              <span style={{ color: sec.iconColor }}>{sec.label}</span>
            </span>
          </span>
        ))}
      </div>

      {/* The three sections */}
      <div className="space-y-3">
        {SECTIONS.map((sec) => {
          const items = songs.map((s, i) => ({ s, i })).filter(({ s }) => s.section === sec.key);
          const cap = capOf(sec.key);
          const full = items.length >= cap;
          return (
            <div key={sec.key} className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-3" data-testid={`song-section-${sec.key}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span title={sec.label} aria-label={sec.label} data-testid={`song-header-icon-${sec.key}`}>
                    <SectionGlyph sec={sec} className="text-[15px]" />
                  </span>
                  <p className="text-[14px] font-bold" style={{ color: sec.accent }}>{sec.label}</p>
                </div>
                <span className="text-[12px] tabular-nums text-white/55" data-testid={`song-count-${sec.key}`}>{items.length} / {cap}</span>
              </div>
              <p className="text-[11.5px] text-white/35 mb-2">{sec.hint}</p>
              {full && (
                <p className="text-[11px] text-amber-300/80 mb-2" data-testid={`song-cap-note-${sec.key}`}>That's the max — remove one to add another.</p>
              )}
              {items.length === 0 ? (
                <div className="text-center py-4 text-white/25 text-[12.5px]">Nothing here yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {items.map(({ s, i }) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2" data-testid={`song-row-${sec.key}-${i}`}>
                      {s.source === 'manual'
                        ? <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center shrink-0" data-testid={`song-manual-icon-${sec.key}-${i}`}><Pencil className="w-4 h-4 text-white/35" /></div>
                        : s.artwork
                          ? <img src={s.artwork} alt="" className="w-9 h-9 rounded-md object-cover shrink-0" />
                          : <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center shrink-0"><Music className="w-4 h-4 text-white/30" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <p className="text-[13px] font-medium truncate">{s.title}</p>
                          {s.source === 'manual' && (
                            <span className="shrink-0 inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wide text-white/55 bg-white/[0.06] border border-white/10" data-testid={`song-manual-chip-${sec.key}-${i}`}>Typed in</span>
                          )}
                        </div>
                        <p className="text-[11px] text-white/45 truncate">{s.artist}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {SECTIONS.filter((o) => o.key !== sec.key).map((o) => {
                          const targetFull = songs.filter((x) => x.section === o.key).length >= capOf(o.key);
                          return (
                            <button
                              key={o.key}
                              onClick={() => moveSong(i, o.key)}
                              disabled={targetFull}
                              title={targetFull ? `${o.label} is full` : `Move to ${o.label}`}
                              aria-label={targetFull ? `${o.label} is full` : `Move to ${o.label}`}
                              className="w-7 h-7 rounded-md text-[13px] font-bold bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center"
                              style={{ color: targetFull ? undefined : o.iconColor }}
                              data-testid={`song-move-${sec.key}-${i}-to-${o.key}`}
                            >
                              <SectionGlyph sec={o} color={targetFull ? 'rgba(255,255,255,0.4)' : o.iconColor} />
                            </button>
                          );
                        })}
                        <button onClick={() => removeSong(i)} aria-label="Remove song" title="Remove" className="p-1 text-white/25 hover:text-red-400" data-testid={`song-remove-${sec.key}-${i}`}><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="inline-flex items-center gap-1.5 text-[11px] text-white/50 rounded-full px-2.5 py-1" style={{ background: 'rgba(29,185,84,0.06)', border: '1px solid rgba(29,185,84,0.16)' }}>
          <Music className="w-3 h-3 text-[#1DB954]" />30-sec preview only — the DJ plays the full song
        </div>
      </div>

      <SongSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        fieldLabel={eventTitle}
        sections={SECTIONS}
        sectionOf={sectionOf}
        sectionFull={sectionFullMap}
        onAddToSection={addToSection}
      />
    </div>
  );
}
